const EXTERNAL_URL_RE = /https?:\/\/[^\s<>"'`]+/g;

function stripTrailingUrlPunctuation(candidate: string): { url: string; trailingText: string } {
  let url = candidate;
  let trailingText = "";

  while (url.length > 0) {
    const lastChar = url[url.length - 1];

    if (/[.,!?;:]/.test(lastChar)) {
      trailingText = lastChar + trailingText;
      url = url.slice(0, -1);
      continue;
    }

    if (lastChar === ")" || lastChar === "]" || lastChar === "}") {
      const openChar = lastChar === ")" ? "(" : lastChar === "]" ? "[" : "{";
      const openCount = (url.match(new RegExp(`\\${openChar}`, "g")) || []).length;
      const closeCount = (url.match(new RegExp(`\\${lastChar}`, "g")) || []).length;

      if (closeCount > openCount) {
        trailingText = lastChar + trailingText;
        url = url.slice(0, -1);
        continue;
      }
    }

    break;
  }

  return { url, trailingText };
}

export function getExternalUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    return trimmed;
  } catch {
    return null;
  }
}

export function formatExternalUrlForDisplay(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname === "/" ? "" : parsed.pathname;
    return `${parsed.host}${path}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

export type ExternalUrlSegment = { type: "text"; value: string } | { type: "url"; value: string };

export function splitTextByExternalUrls(text: string): ExternalUrlSegment[] {
  if (!text) return [{ type: "text", value: text }];

  const segments: ExternalUrlSegment[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(EXTERNAL_URL_RE)) {
    const startIndex = match.index ?? 0;
    const matchedValue = match[0];

    if (startIndex > lastIndex) {
      segments.push({ type: "text", value: text.slice(lastIndex, startIndex) });
    }

    const { url, trailingText } = stripTrailingUrlPunctuation(matchedValue);
    const externalUrl = getExternalUrl(url);

    if (externalUrl) {
      segments.push({ type: "url", value: externalUrl });
      if (trailingText) {
        segments.push({ type: "text", value: trailingText });
      }
    } else {
      segments.push({ type: "text", value: matchedValue });
    }

    lastIndex = startIndex + matchedValue.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: "text", value: text }];
}
